import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    App,
    Badge,
    Button,
    Col,
    Collapse,
    Descriptions,
    Divider,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {BugOutlined, HistoryOutlined, PlayCircleOutlined, ReloadOutlined} from '@ant-design/icons';
import SockJS from 'sockjs-client';
import {Client, IMessage} from '@stomp/stompjs';
import {AgentDebugDetailResponse, AgentDebugRecord, agentDebugService} from '../../services/agentDebugService';
import {agentRoutingConfigService, AgentTriggerConfig} from '../../services/agentRoutingConfigService';
import {AgentWorkflowStep} from '../../hooks/useAgentWebSocket';
import AgentDebugFlowView from './AgentDebugFlowView';
import AgentDebugNodeDetail from './AgentDebugNodeDetail';

const {Text} = Typography;
const {TextArea} = Input;

const EVENT_TYPE_OPTIONS = [
    {value: 'RADAR_RESULT_READY', label: 'RADAR_RESULT_READY - 雷达结果'},
    {value: 'CONTRACT_TOP_N_READY', label: 'CONTRACT_TOP_N_READY - TopN'},
    {value: 'PATTERN_RESULT_READY', label: 'PATTERN_RESULT_READY - 形态识别'},
    {value: 'ZONE_RESULT_READY', label: 'ZONE_RESULT_READY - 区域识别'},
    {value: 'MANUAL_TRIGGER', label: 'MANUAL_TRIGGER - 手动触发'},
];

const MESSAGE_TYPE_OPTIONS = [
    {value: 'TRADE_PROPOSAL', label: 'TRADE_PROPOSAL - 交易建议'},
    {value: 'WATCH_SIGNAL', label: 'WATCH_SIGNAL - 观望信号'},
    {value: 'RISK_REVIEW_REQUEST', label: 'RISK_REVIEW_REQUEST - 风控请求'},
    {value: 'RISK_REVIEW_REPLY', label: 'RISK_REVIEW_REPLY - 风控回复'},
    {value: 'TRADE_EXECUTION_RESULT', label: 'TRADE_EXECUTION_RESULT - 交易结果'},
    {value: 'TRADE_CANCELLED', label: 'TRADE_CANCELLED - 交易取消'},
    {value: 'TRADE_ACTIONS', label: 'TRADE_ACTIONS - 交易动作'},
    {value: 'TREND_ANALYSIS', label: 'TREND_ANALYSIS - 趋势分析'},
    {value: 'MARKET_REGIME_RECOGNITION', label: 'MARKET_REGIME_RECOGNITION - 市场形态识别'},
    {value: 'FUNDAMENTAL_RISK_CONTROL', label: 'FUNDAMENTAL_RISK_CONTROL - 基本面风控'},
    {value: 'TRADE_PLAN', label: 'TRADE_PLAN - 交易规划'},
];

const EVENT_SAMPLE_JSON: Record<string, string> = {
    RADAR_RESULT_READY: JSON.stringify({
        cex: 'OKX',
        exchange: 'OKX',
        timeframe: '1H',
        cacheKey: 'OKX_1H',
        generatedAt: new Date().toISOString(),
        globalLongRatio: 52.35,
        topLongCoins: [{symbol: 'BTC', ratio: 68.5}, {symbol: 'ETH', ratio: 62.1}],
        topShortCoins: [{symbol: 'ADA', ratio: 65.2}, {symbol: 'XRP', ratio: 61.3}]
    }, null, 2),
    CONTRACT_TOP_N_READY: JSON.stringify({
        cex: 'OKX',
        vendor: 'OKX',
        isLiveTrading: 1,
        topN: 30,
        sortBy: 'volume',
        hourStr: new Date().toISOString().slice(0, 13).replace('T', ' '),
        topInstIds: [
            {rank: 1, instId: 'BTC-USDT-SWAP'},
            {rank: 2, instId: 'ETH-USDT-SWAP'},
            {rank: 3, instId: 'SOL-USDT-SWAP'}
        ],
        totalCount: 30
    }, null, 2),
    PATTERN_RESULT_READY: JSON.stringify({
        cex: 'OKX',
        symbol: 'BTC-USDT-SWAP',
        interval: '1H',
        patternCode: 'DOJI',
        patternName: '十字星',
        direction: 1,
        confidence: 75.5,
        refPrice: 67250.30
    }, null, 2),
    ZONE_RESULT_READY: JSON.stringify({
        cex: 'OKX',
        symbol: 'ETH-USDT-SWAP',
        interval: '4H',
        zoneType: 'HIGH',
        patternCode: 'RESISTANCE_ZONE',
        direction: -1,
        confidence: 82.0,
        zoneUpper: 3520.00,
        zoneLower: 3450.50
    }, null, 2),
    MANUAL_TRIGGER: JSON.stringify({
        triggeredBy: 'user',
        instruction: '请分析BTC-USDT-SWAP当前走势',
    }, null, 2),
};

interface AgentDebugModalProps {
    visible: boolean;
    agentId: number;
    agentName: string;
    onClose: () => void;
}

const STATUS_MAP: Record<string, { color: string; label: string }> = {
    SUBMITTED: {color: 'default', label: '已提交'},
    PROCESSING: {color: 'processing', label: '执行中'},
    SUCCESS: {color: 'success', label: '成功'},
    FAILED: {color: 'error', label: '失败'},
};

const AgentDebugModal: React.FC<AgentDebugModalProps> = ({visible, agentId, agentName, onClose}) => {
    const {message: messageApi} = App.useApp();
    const [form] = Form.useForm();
    const [triggerKind, setTriggerKind] = useState<'EVENT' | 'MESSAGE'>('EVENT');
    const [submitting, setSubmitting] = useState(false);
    const [currentRun, setCurrentRun] = useState<AgentDebugDetailResponse | null>(null);
    const [history, setHistory] = useState<AgentDebugRecord[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [wsSteps, setWsSteps] = useState<AgentWorkflowStep[]>([]);
    const [selectedStep, setSelectedStep] = useState<AgentWorkflowStep | null>(null);
    const [wsConnected, setWsConnected] = useState(false);
    const [agentTriggers, setAgentTriggers] = useState<AgentTriggerConfig[]>([]);
    const currentTraceIdRef = useRef<string | null>(null);
    const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stompClientRef = useRef<Client | null>(null);

    const loadHistory = useCallback(async () => {
        if (!agentId) return;
        setHistoryLoading(true);
        try {
            const data = await agentDebugService.getHistory(agentId, 20);
            setHistory(data);
        } catch {
        } finally {
            setHistoryLoading(false);
        }
    }, [agentId]);

    const disconnectWs = useCallback(() => {
        if (stompClientRef.current) {
            try {
                stompClientRef.current.deactivate();
            } catch {
            }
            stompClientRef.current = null;
        }
        setWsConnected(false);
    }, []);

    const connectWs = useCallback((traceId: string) => {
        disconnectWs();
        currentTraceIdRef.current = traceId;
        const token = localStorage.getItem('auth_token');
        const client = new Client({
            webSocketFactory: () => new SockJS('/ws/chat'),
            connectHeaders: token ? {Authorization: `Bearer ${token}`} : {},
            debug: () => {
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
        });

        client.onConnect = () => {
            setWsConnected(true);
            client.subscribe('/topic/bot/agent', (message: IMessage) => {
                try {
                    const body = JSON.parse(message.body);
                    if (body.type === 'agent_workflow' && body.traceId
                        && body.traceId === currentTraceIdRef.current) {
                        setWsSteps(prev => {
                            const exists = prev.some(s =>
                                s.turn === body.turn && s.functionName === body.functionName
                            );
                            if (exists) return prev;
                            const step: AgentWorkflowStep = {
                                type: body.type,
                                traceId: body.traceId,
                                sessionId: body.sessionId,
                                decisionId: body.decisionId,
                                turn: body.turn,
                                stepType: body.stepType,
                                step: body.step,
                                functionName: body.functionName,
                                arguments: body.arguments,
                                result: body.result,
                                summary: body.summary,
                                source: body.source,
                                outputType: body.outputType,
                                processingTimeMs: body.processingTimeMs,
                                timestamp: body.timestamp || Date.now(),
                            };
                            return [...prev, step];
                        });
                    }
                } catch {
                }
            });
        };

        client.onDisconnect = () => {
            setWsConnected(false);
        };

        client.onStompError = () => {
            setWsConnected(false);
        };

        client.activate();
        stompClientRef.current = client;
    }, [disconnectWs]);

    useEffect(() => {
        if (visible && agentId > 0) {
            form.resetFields();
            form.setFieldsValue({triggerKind: 'EVENT', eventType: undefined, payloadJson: ''});
            setTriggerKind('EVENT');
            setCurrentRun(null);
            setWsSteps([]);
            setSelectedStep(null);
            loadHistory();
            stopPolling();
            disconnectWs();
            agentRoutingConfigService.getAllTriggers().then(allTriggers => {
                setAgentTriggers(allTriggers.filter(t => t.agentConfigId === agentId));
            }).catch(() => {
                setAgentTriggers([]);
            });
        }
        return () => {
            stopPolling();
            disconnectWs();
        };
    }, [visible, agentId]);

    const stopPolling = () => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        setPolling(false);
    };

    const startPolling = (recordId: number) => {
        stopPolling();
        setPolling(true);
        pollingTimerRef.current = setInterval(async () => {
            try {
                const detail = await agentDebugService.getDetail(recordId);
                setCurrentRun(detail);
                if (detail.status === 'SUCCESS' || detail.status === 'FAILED') {
                    stopPolling();
                    loadHistory();
                }
            } catch {
                stopPolling();
            }
        }, 2000);
    };

    const validateJson = (value: string): boolean => {
        if (!value || value.trim().length === 0) {
            setJsonError('载荷JSON不能为空');
            return false;
        }
        try {
            JSON.parse(value);
            setJsonError(null);
            return true;
        } catch {
            setJsonError('JSON格式不合法，请检查');
            return false;
        }
    };

    const handleJsonChange = (value: string) => {
        form.setFieldsValue({payloadJson: value});
        if (value && value.trim().length > 0) {
            validateJson(value);
        } else {
            setJsonError(null);
        }
    };

    const handleExecute = async () => {
        try {
            const values = await form.validateFields();
            if (!validateJson(values.payloadJson)) {
                return;
            }
            setSubmitting(true);
            setCurrentRun(null);
            setWsSteps([]);
            setSelectedStep(null);
            const result = await agentDebugService.executeDebug({
                agentConfigId: agentId,
                triggerKind: values.triggerKind,
                eventType: values.eventType,
                payloadJson: values.payloadJson,
            });
            const detail: AgentDebugDetailResponse = {
                id: result.recordId,
                agentConfigId: agentId,
                agentName,
                triggerKind: values.triggerKind,
                eventType: values.eventType,
                payloadJson: values.payloadJson,
                traceId: result.traceId,
                status: result.status,
                resultSummary: '',
                nodeCount: 0,
                totalTimeMs: 0,
                userId: 0,
                flowNodes: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            setCurrentRun(detail);
            connectWs(result.traceId);
            startPolling(result.recordId);
            messageApi.success('调试请求已提交');
        } catch (error: any) {
            if (error?.message) messageApi.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplay = async (record: AgentDebugRecord) => {
        try {
            setSubmitting(true);
            setCurrentRun(null);
            setWsSteps([]);
            setSelectedStep(null);
            const result = await agentDebugService.replayDebug(record.id);
            const detail: AgentDebugDetailResponse = {
                id: result.recordId,
                agentConfigId: agentId,
                agentName,
                triggerKind: record.triggerKind,
                eventType: record.eventType,
                payloadJson: record.payloadJson,
                traceId: result.traceId,
                status: result.status,
                resultSummary: '',
                nodeCount: 0,
                totalTimeMs: 0,
                userId: 0,
                flowNodes: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            setCurrentRun(detail);
            form.setFieldsValue({
                triggerKind: record.triggerKind,
                eventType: record.eventType,
                payloadJson: record.payloadJson,
            });
            setTriggerKind(record.triggerKind as 'EVENT' | 'MESSAGE');
            connectWs(result.traceId);
            startPolling(result.recordId);
            messageApi.success('重放调试已提交');
        } catch (error: any) {
            if (error?.message) messageApi.error(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRefreshDetail = async () => {
        if (!currentRun?.id) return;
        try {
            const detail = await agentDebugService.getDetail(currentRun.id);
            setCurrentRun(detail);
            form.setFieldsValue({
                triggerKind: detail.triggerKind,
                eventType: detail.eventType,
                payloadJson: detail.payloadJson,
            });
            setTriggerKind(detail.triggerKind as 'EVENT' | 'MESSAGE');
            if (detail.status === 'SUCCESS' || detail.status === 'FAILED') {
                stopPolling();
            }
        } catch {
        }
    };

    const handleSelectType = (value: string) => {
        form.setFieldsValue({eventType: value});
        const sample = EVENT_SAMPLE_JSON[value];
        if (sample) {
            form.setFieldsValue({payloadJson: sample});
        }
    };

    const handleNodeClick = (step: AgentWorkflowStep, index: number) => {
        setSelectedStep(prev => prev === step ? null : step);
    };

    const statusTag = (status: string) => {
        const s = STATUS_MAP[status] || {color: 'default', label: status};
        return (
            <Space>
                <Badge status={s.color as any}/>
                <Text>{s.label}</Text>
            </Space>
        );
    };

    const renderResult = () => {
        if (!currentRun) return null;
        const isTerminal = currentRun.status === 'SUCCESS' || currentRun.status === 'FAILED';
        const isSuccess = currentRun.status === 'SUCCESS';
        const showFlow = wsSteps.length > 0 || currentRun.traceId;

        return (
            <div style={{
                marginTop: 16,
                padding: 16,
                border: `1px solid ${isTerminal ? (isSuccess ? 'rgba(0, 217, 126, 0.3)' : 'rgba(255, 82, 82, 0.3)') : 'rgba(240,180,41,0.3)'}`,
                borderRadius: 8,
                background: isTerminal ? (isSuccess ? 'rgba(0, 217, 126, 0.05)' : 'rgba(255, 82, 82, 0.05)') : 'rgba(240,180,41,0.05)'
            }}>
                <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                    <Col>
                        <Space>
                            <BugOutlined
                                style={{color: isTerminal ? (isSuccess ? 'var(--pt-bull)' : 'var(--pt-bear)') : 'var(--pt-primary)'}}/>
                            <Text strong style={{color: 'rgba(255,255,255,0.92)'}}>执行结果</Text>
                            {statusTag(currentRun.status)}
                            {polling && <Badge status="processing" text="监控中"/>}
                            {wsConnected && <Badge status="success" text="实时"/>}
                        </Space>
                    </Col>
                    <Col>
                        <Tooltip title="刷新">
                            <Button type="text" size="small" icon={<ReloadOutlined/>}
                                    onClick={handleRefreshDetail}/>
                        </Tooltip>
                    </Col>
                </Row>
                <Descriptions column={2} size="small" style={{marginTop: 8}}>
                    <Descriptions.Item label="Trace ID">
                        <Text copyable style={{fontFamily: 'monospace', fontSize: 12}}>
                            {currentRun.traceId}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="耗时">
                        {currentRun.totalTimeMs > 0 ? `${currentRun.totalTimeMs}ms` : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="事件类型">
                        <Tag>{currentRun.eventType}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="触发方式">
                        <Tag>{currentRun.triggerKind}</Tag>
                    </Descriptions.Item>
                </Descriptions>

                {showFlow && (
                    <div style={{marginTop: 12}}>
                        <Text strong style={{
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 13,
                            marginBottom: 8,
                            display: 'block'
                        }}>
                            {'>'} 执行流程
                        </Text>
                        <AgentDebugFlowView
                            steps={wsSteps}
                            loading={!isTerminal && wsSteps.length === 0}
                            traceId={currentRun.traceId}
                            completed={isTerminal}
                            failed={isTerminal && !isSuccess}
                            onNodeClick={handleNodeClick}
                        />
                        <AgentDebugNodeDetail step={selectedStep} visible={!!selectedStep}/>
                    </div>
                )}

                {currentRun.resultSummary && (
                    <Collapse
                        size="small"
                        ghost
                        style={{marginTop: 8}}
                        items={[{
                            key: 'summary',
                            label: <Text style={{fontSize: 12, color: 'rgba(255,255,255,0.65)'}}>
                                执行摘要
                            </Text>,
                            children: (
                                <pre style={{
                                    background: 'rgba(0,0,0,0.15)', borderRadius: 4, padding: 8,
                                    fontSize: 12, color: 'rgba(255,255,255,0.75)',
                                    fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                                    maxHeight: 150, overflow: 'auto', margin: 0
                                }}>
                                    {currentRun.resultSummary}
                                </pre>
                            )
                        }]}
                    />
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <BugOutlined style={{color: 'var(--pt-primary)'}}/>
                    <span>智能体调试 - {agentName}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={960}
            footer={null}
            destroyOnHidden
        >
            <div style={{marginBottom: 16}}>
                <Text strong style={{color: 'rgba(255,255,255,0.92)', fontSize: 14}}>
                    <PlayCircleOutlined style={{marginRight: 6}}/>
                    调试触发
                </Text>
            </div>
            <Form form={form} layout="vertical" initialValues={{triggerKind: 'EVENT'}}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="triggerKind" label="触发方式" rules={[{required: true}]}>
                            <Select
                                onChange={(v) => {
                                    setTriggerKind(v);
                                    form.setFieldsValue({eventType: undefined, payloadJson: ''});
                                }}
                                options={[
                                    {value: 'EVENT', label: '事件触发'},
                                    {value: 'MESSAGE', label: '消息触发'},
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={18}>
                        <Form.Item name="eventType" label="事件/消息类型"
                                   rules={[{required: true, message: '请选择类型'}]}>
                            <Select
                                placeholder="选择类型"
                                onChange={handleSelectType}
                                options={triggerKind === 'EVENT'
                                    ? (() => {
                                        const configured = new Set(
                                            agentTriggers.filter(t => t.triggerKind === 'EVENT')
                                                .map(t => t.eventType).filter(Boolean)
                                        );
                                        return configured.size > 0
                                            ? EVENT_TYPE_OPTIONS.filter(o => configured.has(o.value))
                                            : EVENT_TYPE_OPTIONS;
                                    })()
                                    : (() => {
                                        const configured = new Set(
                                            agentTriggers.filter(t => t.triggerKind === 'MESSAGE')
                                                .map(t => t.messageType).filter(Boolean)
                                        );
                                        return configured.size > 0
                                            ? MESSAGE_TYPE_OPTIONS.filter(o => configured.has(o.value))
                                            : MESSAGE_TYPE_OPTIONS;
                                    })()}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="payloadJson" label="载荷JSON"
                           rules={[{required: true, message: '请输入载荷JSON'}]}
                           validateStatus={jsonError ? 'error' : undefined}
                           help={jsonError || undefined}>
                    <TextArea rows={6} placeholder="输入载荷JSON" maxLength={20000} showCount
                              autoSize={{minRows: 4, maxRows: 12}}
                              style={{fontFamily: 'monospace', fontSize: 12}}
                              onChange={(e) => handleJsonChange(e.target.value)}/>
                </Form.Item>
                <Row justify="end">
                    <Col>
                        <Button type="primary" icon={<BugOutlined/>} onClick={handleExecute}
                                loading={submitting}>
                            执行调试
                        </Button>
                    </Col>
                </Row>
            </Form>

            {renderResult()}

            <Divider/>

            <div style={{marginBottom: 8}}>
                <Space>
                    <HistoryOutlined style={{color: 'rgba(255,255,255,0.65)'}}/>
                    <Text strong style={{color: 'rgba(255,255,255,0.92)', fontSize: 14}}>
                        调试历史
                    </Text>
                </Space>
            </div>
            <Table
                dataSource={history}
                rowKey="id"
                loading={historyLoading}
                size="small"
                pagination={{pageSize: 5, showTotal: (t) => `共 ${t} 条`}}
                locale={{emptyText: '暂无调试记录'}}
                style={{marginTop: 8}}
            >
                <Table.Column title="时间" width={160} render={(_, r: AgentDebugRecord) => {
                    if (!r.createdAt) return '';
                    return new Date(r.createdAt).toLocaleString('zh-CN');
                }}/>
                <Table.Column title="类型" width={80} render={(_, r: AgentDebugRecord) => (
                    <Tag>{r.triggerKind}</Tag>
                )}/>
                <Table.Column title="事件" ellipsis render={(_, r: AgentDebugRecord) => (
                    <Text style={{fontSize: 12, fontFamily: 'monospace'}}>{r.eventType}</Text>
                )}/>
                <Table.Column title="状态" width={80} render={(_, r: AgentDebugRecord) => (
                    statusTag(r.status)
                )}/>
                <Table.Column title="操作" width={80} render={(_, r: AgentDebugRecord) => (
                    <Space size={0}>
                        <Tooltip title="查看详情">
                            <Button type="text" size="small" icon={<HistoryOutlined/>}
                                    onClick={async () => {
                                        try {
                                            const detail = await agentDebugService.getDetail(r.id);
                                            setCurrentRun(detail);
                                            setWsSteps([]);
                                            setSelectedStep(null);
                                            form.setFieldsValue({
                                                triggerKind: detail.triggerKind,
                                                eventType: detail.eventType,
                                                payloadJson: detail.payloadJson,
                                            });
                                            setTriggerKind(detail.triggerKind as 'EVENT' | 'MESSAGE');
                                            if (detail.status === 'PROCESSING') {
                                                startPolling(detail.id);
                                            }
                                        } catch (e: any) {
                                            messageApi.error(e.message || '获取详情失败');
                                        }
                                    }}/>
                        </Tooltip>
                        <Tooltip title="重放">
                            <Button type="text" size="small" icon={<PlayCircleOutlined/>}
                                    onClick={() => handleReplay(r)}/>
                        </Tooltip>
                    </Space>
                )}/>
            </Table>
        </Modal>
    );
};

export default AgentDebugModal;
